import React, { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api';
import AuditTrail from './AuditTrail';
import { categoryOptions, INVITATION_CATEGORY, joinGuests, splitGuests, VAT_EXCLUDED_CATEGORIES } from '../utils/expenseCategories';

export type AdminExpense = {
  id: number;
  user_id: number;
  user_name: string;
  user_email: string;
  date: string;
  type: string;
  justification: string;
  amount: number;
  currency: string;
  reimbursement_amount: number;
  reimbursement_currency: string;
  exchange_rate: number;
  fetched_exchange_rate?: number;
  override_exchange_rate?: number;
  override_reason?: string;
  status: 'draft' | 'submitted' | 'approved' | 'rejected' | 'paid';
  receipt: string | null;
  admin_notes: string | null;
  approved_by_email: string | null;
  approved_at: string | null;
  created_at: string;
  unique_id?: string;
  expense_id?: string;
  classification?: string | null;
  vat_amount?: number | null;
};

export interface AuditItem {
  id: number;
  expense_id: number;
  actor_email: string;
  actor_role: string;
  change_type: string;
  old_value: any;
  new_value: any;
  deviation_pct?: number | null;
  implied_rate?: number | null;
  reason?: string | null;
  created_at: string;
}

interface Props {
  isOpen: boolean;
  expense: AdminExpense;
  onClose: () => void;
  onSaved: () => void;
}

const currencyRegex = /^[A-Z]{3}$/;

const AdminExpenseEditModal: React.FC<Props> = ({ isOpen, expense, onClose, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const token = (typeof window !== 'undefined') ? localStorage.getItem('token') : '';
  const [userRole, setUserRole] = useState<string>('');

  // Local edit fields
  const [origAmount, setOrigAmount] = useState<number>(expense.amount);
  const [date, setDate] = useState<string>(expense.date ? String(expense.date).split('T')[0] : '');
  // Currency handling: support predefined list + OTHER (custom)
  const currencies = ['EUR', 'USD', 'KES', 'NGN', 'ZAR', 'XOF', 'XAF', 'GHS', 'CHF', 'GBP', 'MAD', 'EGP', 'OTHER'];
  const currencyNames: { [key: string]: string } = {
    EUR: 'Euro',
    USD: 'US Dollar',
    KES: 'Kenyan Shilling',
    NGN: 'Nigerian Naira',
    ZAR: 'South African Rand',
    XOF: 'West African CFA Franc',
    XAF: 'Central African CFA Franc',
    GHS: 'Ghanaian Cedi',
    CHF: 'Swiss Franc',
    GBP: 'British Pound',
    MAD: 'Moroccan Dirham',
    EGP: 'Egyptian Pound',
    OTHER: 'Other (specify)'
  };
  const predefinedSet = new Set(currencies);
  const [origCurrency, setOrigCurrency] = useState<string>(predefinedSet.has(expense.currency) ? expense.currency : 'OTHER');
  const [customOrigCurrency, setCustomOrigCurrency] = useState<string>(predefinedSet.has(expense.currency) ? '' : expense.currency.toUpperCase());
  const isOtherCurrency = origCurrency === 'OTHER';
  const getFinalOrigCurrency = () => (isOtherCurrency ? customOrigCurrency : origCurrency);
  const [currencyError, setCurrencyError] = useState<string>('');

  const [clearOverride, setClearOverride] = useState<boolean>(false);

  const [category, setCategory] = useState<string>(expense.type);
  const [justification, setJustification] = useState<string>(expense.justification);
  const [guestNames, setGuestNames] = useState<string>('');
  const isInvitation = category === INVITATION_CATEGORY;
  const finalJustification = isInvitation ? joinGuests(justification, guestNames) : justification;
  // Classification selection to mirror creation form
  const classificationOptions = ['', 'support', 'investment', 'mix'];
  const [classification, setClassification] = useState<string>((expense.classification || '') as string);
  const [vatAmount, setVatAmount] = useState<string>(
    expense.vat_amount !== null && expense.vat_amount !== undefined ? String(expense.vat_amount) : ''
  );
  const [showVatField, setShowVatField] = useState<boolean>(false);

  const [reimAmount, setReimAmount] = useState<string>(String(expense.reimbursement_amount || ''));
  const [reimReason, setReimReason] = useState<string>('');
  const [reimTouched, setReimTouched] = useState<boolean>(false);

  const [fetchedRate, setFetchedRate] = useState<number | null>(expense.fetched_exchange_rate || expense.exchange_rate || null);
  const [isLoadingRate, setIsLoadingRate] = useState<boolean>(false);

  // Derived implied rate when editing reimbursement
  const impliedRate = useMemo(() => {
    if (!reimTouched) return null; // Only compute when user is overriding
    const amt = Number(reimAmount);
    if (!isFinite(amt) || amt <= 0) return null;
    const origAmt = Number(origAmount);
    if (!isFinite(origAmt) || origAmt <= 0) return null;
    // reimbursement = original_amount * rate (assuming same base)
    const rate = amt / origAmt;
    return isFinite(rate) ? rate : null;
  }, [reimAmount, origAmount, reimTouched]);

  const deviationPct = useMemo(() => {
    if (!impliedRate || !fetchedRate) return null;
    // Use 4-decimal precision for comparison per requirement, but keep sign
    const round4 = (n: number) => Math.round(n * 10000) / 10000;
    const implied4 = round4(impliedRate);
    const base4 = round4(fetchedRate);
    if (!isFinite(implied4) || !isFinite(base4) || base4 === 0) return null;
    const ratio = (implied4 - base4) / base4;
    let pct = ratio * 100;
    // Treat very small rounding-only differences as zero
    if (Math.abs(pct) < 0.01) pct = 0;
    return pct;
  }, [impliedRate, fetchedRate]);

  // Audit timeline
  const [audit, setAudit] = useState<AuditItem[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    // Reset fields to current expense when opened
    setOrigAmount(expense.amount);
    setDate(expense.date ? String(expense.date).split('T')[0] : '');
    if (predefinedSet.has(expense.currency)) {
      setOrigCurrency(expense.currency);
      setCustomOrigCurrency('');
    } else {
      setOrigCurrency('OTHER');
      setCustomOrigCurrency((expense.currency || '').toUpperCase());
    }
    setClearOverride(false);
    setCategory(expense.type);
    if (expense.type === INVITATION_CATEGORY) {
      const { description, guests } = splitGuests(expense.justification);
      setJustification(description);
      setGuestNames(guests);
    } else {
      setJustification(expense.justification);
      setGuestNames('');
    }
    // Normalize existing classification to our UI values if possible
    if (expense.classification) {
      const raw = String(expense.classification).toLowerCase();
      const hasSupport = raw.includes('support');
      const hasCoreOrInvestment = raw.includes('core') || raw.includes('investment');
      const hasMix = raw.includes('mix');
      let normalized = '';
      if ((hasSupport && hasCoreOrInvestment) || hasMix) normalized = 'mix';
      else if (hasSupport) normalized = 'support';
      else if (hasCoreOrInvestment) normalized = 'investment';
      else if (['support', 'investment', 'mix'].includes(raw)) normalized = raw;
      setClassification(normalized);
    } else {
      setClassification('');
    }
    setVatAmount(expense.vat_amount !== null && expense.vat_amount !== undefined ? String(expense.vat_amount) : '');
    setReimAmount(String(expense.reimbursement_amount || ''));
    setReimReason('');
    setReimTouched(false);
    setFetchedRate(expense.fetched_exchange_rate || expense.exchange_rate || null);
    // Load audit
    (async () => {
      try {
        setAuditLoading(true);
        const resp = await api.get(
          `/api/admin/expenses/${expense.id}/audit`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const history = (resp.data?.history || []) as any[];
        setAudit(history);
      } catch (e) {
        setAudit([]);
      } finally {
        setAuditLoading(false);
      }
    })();
  }, [isOpen, expense, token]);

  // Decode role from JWT
  useEffect(() => {
    if (!token) return;
    try {
      const payload = JSON.parse(atob(String(token).split('.')[1] || ''));
      const role = payload?.role || '';
      setUserRole(role);
    } catch (e) {
      // ignore
      setUserRole('');
    }
  }, [token]);

  // VAT visibility logic to mirror creation form
  useEffect(() => {
    const finalCur = getFinalOrigCurrency();
    const isVatApplicable = (classification === 'support' || classification === 'mix') && finalCur === 'EUR' && !VAT_EXCLUDED_CATEGORIES.includes(category);
    setShowVatField(isVatApplicable);
    if (!isVatApplicable) {
      // Keep existing value if previously stored? Creation form clears; follow same behavior and clear.
      setVatAmount('');
    }
  }, [classification, origCurrency, customOrigCurrency, category]);

  // Auto-fetch FX rate for super admins when date or original currency changes
  const lastFxQueryRef = useRef<string>('');
  useEffect(() => {
    const finalCur = getFinalOrigCurrency();
    const reimbCur = expense.reimbursement_currency;

    // Only super admins auto-refresh FX
    if (userRole !== 'super_admin') return;
    if (!date || !finalCur || !reimbCur) return;

    // Same currency shortcut
    if (finalCur === reimbCur) {
      setFetchedRate(1.0);
      setIsLoadingRate(false);
      return;
    }

    // Basic date validation similar to SubmitExpense rules (no future, not older than 12 months)
    const today = new Date();
    const selected = new Date(date);
    const twelveMonthsAgo = new Date();
    twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);
    if (selected > today || selected < twelveMonthsAgo) {
      return;
    }

    // Avoid fetching if no change vs persisted expense (initial open)
    const origDateStr = expense.date ? String(expense.date).split('T')[0] : '';
    const origCurStr = expense.currency;
    if (date === origDateStr && finalCur === origCurStr) {
      // Keep existing fetchedRate set from expense; do not refetch
      setFetchedRate(expense.fetched_exchange_rate || expense.exchange_rate || null);
      setIsLoadingRate(false);
      return;
    }

    // Avoid duplicate request for the same params
    const queryKey = `${finalCur}|${reimbCur}|${date}`;
    if (lastFxQueryRef.current === queryKey) {
      return;
    }

    let cancelled = false;
    setIsLoadingRate(true);
    (async () => {
      try {
        const resp = await api.get(
          `/api/exchange-rate?from=${finalCur}&to=${reimbCur}&date=${date}`
        );
        const rate = resp.data?.rate;
        if (cancelled) return;
        if (typeof rate === 'number' && isFinite(rate) && rate > 0) {
          setFetchedRate(rate);
          lastFxQueryRef.current = queryKey;
        }
      } catch (e) {
        // Fail silently in UI; keep previous rate
      } finally {
        if (!cancelled) setIsLoadingRate(false);
      }
    })();
    return () => {
      cancelled = true;
      // Ensure spinner resets if request is canceled due to dependency change
      setIsLoadingRate(false);
    };
  }, [userRole, date, origCurrency, customOrigCurrency, expense.currency, expense.date, expense.reimbursement_currency]);

  if (!isOpen) return null;

  const formatCurrency = (amount: number | null | undefined, currency: string = '') => {
    if (amount === null || amount === undefined || isNaN(Number(amount))) return 'N/A';
    const formattedAmount = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(amount));
    return currency ? `${formattedAmount} ${currency}` : `${formattedAmount}`;
  };

  const formatRate = (rate: number | null | undefined) => {
    if (rate === null || rate === undefined || isNaN(Number(rate))) return '—';
    return Number(rate).toFixed(4);
  };

  const canSubmit = () => {
    // At least one section changed
    const finalCur = getFinalOrigCurrency();
    const origDateStr = expense.date ? String(expense.date).split('T')[0] : '';
    const changedOriginal = Number(origAmount) !== Number(expense.amount)
      || finalCur !== expense.currency
      || date !== origDateStr
      || clearOverride;
    const changedMetadata = category !== expense.type || finalJustification !== expense.justification || (classification || '') !== (expense.classification || '') || (vatAmount || '') !== (expense.vat_amount !== null && expense.vat_amount !== undefined ? String(expense.vat_amount) : '');
    // Only consider reimbursement changed if user manually edited it
    const changedReim = reimTouched && ((reimAmount || '') !== String(expense.reimbursement_amount || ''));
    // Basic validations
    if (isInvitation && !guestNames.trim()) return false;
    if (changedOriginal) {
      if (!isFinite(Number(origAmount)) || Number(origAmount) <= 0) return false;
      if (isOtherCurrency) {
        if (!currencyRegex.test(String(customOrigCurrency).toUpperCase())) return false;
      } else {
        if (!currencyRegex.test(String(origCurrency).toUpperCase())) return false;
      }
    }
    if (changedReim) {
      if (!isFinite(Number(reimAmount)) || Number(reimAmount) <= 0) return false;
      if (!reimReason.trim()) return false;
    }
    return changedOriginal || changedMetadata || changedReim;
  };

  const handleSubmit = async () => {
    setError(null);
    setLoading(true);
    try {
      const body: any = {};
      // Original
      const finalCur = getFinalOrigCurrency().toUpperCase();
      const origDateStr = expense.date ? String(expense.date).split('T')[0] : '';
      const changedOriginal = Number(origAmount) !== Number(expense.amount)
        || finalCur !== expense.currency
        || date !== origDateStr
        || clearOverride;
      if (changedOriginal) {
        body.original = {
          amount: Number(origAmount),
          currency: finalCur,
          date: date
        };
        if (clearOverride) body.clear_override = true;
      }
      // Metadata
      const changedMetadata = category !== expense.type || finalJustification !== expense.justification || (classification || '') !== (expense.classification || '') || (vatAmount || '') !== (expense.vat_amount !== null && expense.vat_amount !== undefined ? String(expense.vat_amount) : '');
      if (changedMetadata) {
        body.metadata = {
          category: category,
          justification: finalJustification,
          classification: classification || undefined,
          vat_amount: (vatAmount !== '' ? Number(vatAmount) : null)
        };
      }
      // Reimbursement (only when manually overridden)
      const changedReim = reimTouched && ((reimAmount || '') !== String(expense.reimbursement_amount || ''));
      if (changedReim) {
        body.reimbursement = {
          amount: Number(reimAmount),
          reason: reimReason.trim()
        };
      }

      const resp = await api.put(
        `/api/admin/expenses/${expense.id}`,
        body,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      onSaved();
      onClose();
    } catch (e: any) {
      console.error('Admin edit failed', e);
      setError(e?.response?.data?.message || 'Failed to save changes');
    } finally {
      setLoading(false);
    }
  };

  const snapshotData = useMemo(() => {
    const normalize = (src: any) => {
      if (!src) src = {};
      const base = src.original && typeof src.original === 'object' ? src.original : src;
      const dateVal = base?.date ?? src?.date ?? expense.date;
      const amountVal = base?.amount ?? src?.amount ?? expense.amount;
      const currencyVal = base?.currency ?? src?.currency ?? expense.currency;
      const reimbursementVal = src?.reimbursement_amount ?? base?.reimbursement_amount ?? expense.reimbursement_amount;
      const fetchedRateVal = base?.fetched_exchange_rate ?? src?.fetched_exchange_rate;
      const exchangeRateVal = base?.exchange_rate ?? src?.exchange_rate;

      const deriveRate = (): number | null => {
        if (typeof fetchedRateVal === 'number' && isFinite(fetchedRateVal) && fetchedRateVal > 0) return fetchedRateVal;
        if (typeof exchangeRateVal === 'number' && isFinite(exchangeRateVal) && exchangeRateVal > 0) return exchangeRateVal;
        const amt = Number(amountVal);
        const reimb = Number(reimbursementVal);
        if (isFinite(amt) && amt > 0 && isFinite(reimb)) return reimb / amt;
        return null;
      };

      return {
        date: dateVal,
        amount: amountVal,
        currency: currencyVal,
        reimbursement: reimbursementVal,
        rate: deriveRate(),
      };
    };

    let original: any = null;
    if (audit && audit.length > 0) {
      const sorted = [...audit].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
      const oldest = sorted[0];
      if (oldest) {
        original = oldest.old_value || oldest.new_value || null;
        if (typeof original === 'string') {
          try {
            original = JSON.parse(original);
          } catch (e) {
            original = oldest.old_value || oldest.new_value || null;
          }
        }
      }
    }

    return {
      original: normalize(original || expense),
      current: normalize({
        date: expense.date,
        amount: expense.amount,
        currency: expense.currency,
        reimbursement_amount: expense.reimbursement_amount,
        exchange_rate: expense.override_exchange_rate ?? expense.exchange_rate ?? null,
      }),
    };
  }, [audit, expense]);

  if (!isOpen) return null;

  return (
    <div className="admin-dialog-overlay">
      <div className="admin-dialog">
        <header className="admin-dialog__header">
          <h2 className="admin-dialog__title">Admin Edit Expense</h2>
          <button className="admin-dialog__close" onClick={onClose} aria-label="Close">×</button>
        </header>

        <div className="admin-dialog__body">
          <div className="admin-dialog__columns admin-dialog__columns--stack">
            <section className="admin-dialog__section">
              <div className="admin-dialog__section-header">
                <h3 className="admin-dialog__section-title">Original Amount &amp; Currency</h3>
                <label className="admin-dialog__checkbox-row">
                  <input type="checkbox" checked={clearOverride} onChange={(e) => setClearOverride(e.target.checked)} />
                  Clear FX override
                </label>
              </div>

              <div className="admin-dialog__field">
                <label className="admin-dialog__label" htmlFor="admin-edit-date">Date</label>
                <input
                  id="admin-edit-date"
                  type="date"
                  className="admin-dialog__input"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
              </div>

              {userRole === 'super_admin' && (
                <div className="admin-dialog__helper">
                  {isLoadingRate
                    ? 'Fetching FX rate…'
                    : (fetchedRate ? `Fetched rate: ${formatRate(fetchedRate)} ${getFinalOrigCurrency()}/${expense.reimbursement_currency}` : '—')}
                </div>
              )}

              <div className="admin-dialog__field-grid admin-dialog__field-grid--tight">
                <div className="admin-dialog__field">
                  <label className="admin-dialog__label" htmlFor="admin-edit-amount">Amount</label>
                  <input
                    id="admin-edit-amount"
                    type="number"
                    step="0.01"
                    inputMode="decimal"
                    className="admin-dialog__input"
                    value={origAmount}
                    onChange={(e) => setOrigAmount(Number(e.target.value))}
                  />
                </div>
                <div className="admin-dialog__field">
                  <label className="admin-dialog__label" htmlFor="admin-edit-currency">Currency</label>
                  <select
                    id="admin-edit-currency"
                    className="admin-dialog__select"
                    value={origCurrency}
                    onChange={(e) => {
                      setOrigCurrency(e.target.value);
                      setCurrencyError('');
                      if (e.target.value !== 'OTHER') setCustomOrigCurrency('');
                    }}
                  >
                    {currencies.map((c) => (
                      <option key={c} value={c}>{c} {currencyNames[c] ? `- ${currencyNames[c]}` : ''}</option>
                    ))}
                  </select>
                  {isOtherCurrency && (
                    <div className="admin-dialog__stack">
                      <input
                        className="admin-dialog__input"
                        value={customOrigCurrency}
                        onChange={(e) => {
                          const v = e.target.value.toUpperCase();
                          setCustomOrigCurrency(v);
                          if (v && !currencyRegex.test(v)) setCurrencyError('Please enter a valid 3-letter currency code (e.g., USD, EUR)');
                          else setCurrencyError('');
                        }}
                        placeholder="Enter 3-letter code"
                        maxLength={3}
                        style={{ textTransform: 'uppercase' as const }}
                      />
                      {currencyError && <div className="admin-dialog__error-text">{currencyError}</div>}
                    </div>
                  )}
                </div>
              </div>

              <div className="admin-dialog__helper">
                Current: {formatCurrency(expense.amount, expense.currency)} → Reimburses in {expense.reimbursement_currency}
              </div>
            </section>

            <section className="admin-dialog__section">
              <h3 className="admin-dialog__section-title">Metadata</h3>
              <div className="admin-dialog__field-grid">
                <div className="admin-dialog__field">
                  <label className="admin-dialog__label" htmlFor="admin-edit-category">Category</label>
                  <select
                    id="admin-edit-category"
                    className="admin-dialog__select"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                  >
                    {categoryOptions(expense.type).map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div className="admin-dialog__field">
                  <label className="admin-dialog__label" htmlFor="admin-edit-classification">Classification</label>
                  <select
                    id="admin-edit-classification"
                    className="admin-dialog__select"
                    value={classification}
                    onChange={(e) => setClassification(e.target.value)}
                  >
                    {classificationOptions.map(opt => (
                      <option key={opt} value={opt}>{opt === '' ? '— Select —' : opt}</option>
                    ))}
                  </select>
                </div>
                <div className="admin-dialog__field admin-dialog__field--full">
                  <label className="admin-dialog__label" htmlFor="admin-edit-justification">Justification</label>
                  <textarea
                    id="admin-edit-justification"
                    className="admin-dialog__textarea"
                    rows={3}
                    value={justification}
                    onChange={(e) => setJustification(e.target.value)}
                  />
                </div>
                {isInvitation && (
                  <div className="admin-dialog__field admin-dialog__field--full">
                    <label className="admin-dialog__label" htmlFor="admin-edit-guests">Guest name(s) *</label>
                    <input
                      id="admin-edit-guests"
                      className="admin-dialog__input"
                      value={guestNames}
                      onChange={(e) => setGuestNames(e.target.value)}
                      placeholder="e.g. Jane Doe (Acme), John Smith (Acme)"
                    />
                  </div>
                )}
                {showVatField && (
                  <div className="admin-dialog__field">
                    <label className="admin-dialog__label" htmlFor="admin-edit-vat">VAT Amount ({getFinalOrigCurrency()})</label>
                    <input
                      id="admin-edit-vat"
                      type="number"
                      step="0.01"
                      inputMode="decimal"
                      className="admin-dialog__input"
                      value={vatAmount}
                      onChange={(e) => setVatAmount(e.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                )}
              </div>
              {!showVatField && (
                <div className="admin-dialog__helper">VAT not applicable for current selection.</div>
              )}
            </section>

            <section className="admin-dialog__section">
              <h3 className="admin-dialog__section-title">Reimbursement (direct)</h3>
              <div className="admin-dialog__field-grid admin-dialog__field-grid--single">
                <div className="admin-dialog__field">
                  <label className="admin-dialog__label" htmlFor="admin-edit-reimbursement">Reimbursement Amount ({expense.reimbursement_currency})</label>
                  <input
                    id="admin-edit-reimbursement"
                    type="number"
                    step="0.01"
                    inputMode="decimal"
                    className="admin-dialog__input"
                    value={reimAmount}
                    onChange={(e) => { setReimAmount(e.target.value); setReimTouched(true); }}
                    placeholder="0.00"
                  />
                </div>
                <div className="admin-dialog__field">
                  <label className="admin-dialog__label" htmlFor="admin-edit-reason">Reason (required if changing)</label>
                  <input
                    id="admin-edit-reason"
                    className="admin-dialog__input"
                    value={reimReason}
                    onChange={(e) => setReimReason(e.target.value)}
                    placeholder="Why adjust reimbursement?"
                  />
                </div>
              </div>
              <div className="admin-dialog__helper">
                Fetched rate: <strong>{formatRate(fetchedRate)}</strong> • Implied rate: <strong>{impliedRate ? formatRate(impliedRate) : '—'}</strong>
                {reimTouched && deviationPct !== null && (
                  <span>
                    {' '}• Deviation: 
                    <strong
                      style={{
                        color:
                          deviationPct < 0
                            ? '#10b981' // negative: override lower than fetched
                            : deviationPct <= 5
                              ? '#f97316' // positive but within limit
                              : '#ef4444' // above limit
                      }}
                    >
                      {deviationPct.toFixed(2)}%
                    </strong>
                  </span>
                )}
              </div>
            </section>

            <section className="admin-dialog__section admin-dialog__section--white">
              <h3 className="admin-dialog__section-title">Snapshots</h3>
              <div className="admin-dialog__snapshot-grid">
                <div>
                  <div className="admin-dialog__snapshot-title">Originally submitted</div>
                  <div><strong>Date</strong></div>
                  <div className="admin-dialog__helper">{snapshotData.original.date ? new Date(snapshotData.original.date).toLocaleDateString() : '—'}</div>
                  <div><strong>Original</strong></div>
                  <div className="admin-dialog__helper">{formatCurrency(snapshotData.original.amount, snapshotData.original.currency)}</div>
                  <div><strong>Reimb.</strong></div>
                  <div className="admin-dialog__helper">{formatCurrency(snapshotData.original.reimbursement, expense.reimbursement_currency)}</div>
                  <div><strong>Rate</strong></div>
                  <div className="admin-dialog__helper">{formatRate(snapshotData.original.rate)} {snapshotData.original.currency}/{expense.reimbursement_currency}</div>
                </div>
                <div>
                  <div className="admin-dialog__snapshot-title">Current (database)</div>
                  <div><strong>Date</strong></div>
                  <div className="admin-dialog__helper">{snapshotData.current.date ? new Date(snapshotData.current.date).toLocaleDateString() : '—'}</div>
                  <div><strong>Original</strong></div>
                  <div className="admin-dialog__helper">{formatCurrency(snapshotData.current.amount, snapshotData.current.currency)}</div>
                  <div><strong>Reimb.</strong></div>
                  <div className="admin-dialog__helper">{formatCurrency(snapshotData.current.reimbursement, expense.reimbursement_currency)}</div>
                  <div><strong>Rate</strong></div>
                  <div className="admin-dialog__helper">{formatRate(snapshotData.current.rate)} {snapshotData.current.currency}/{expense.reimbursement_currency}</div>
                </div>
              </div>
            </section>

            <section className="admin-dialog__section">
              <AuditTrail 
                expenseId={(expense as any).expense_id || expense.unique_id || ''} 
                token={localStorage.getItem('token') || ''} 
              />
            </section>

          </div>

          {error && <div className="admin-dialog__error-text">{error}</div>}

          <footer className="admin-dialog__footer">
            <button className="admin-dialog__button admin-dialog__button--secondary" onClick={onClose}>Cancel</button>
            <button
              className="admin-dialog__button admin-dialog__button--primary"
              onClick={handleSubmit}
              disabled={!canSubmit() || loading}
            >
              {loading ? 'Saving…' : 'Save changes'}
            </button>
          </footer>
        </div>
      </div>
    </div>
  );
};

export default AdminExpenseEditModal;
